import { useState, useMemo } from "react";
import "../../stylesheets2025/registrationForm.css";

/* =====================================================================
   CONFIG — DUMMY links, baad me real se replace karna
   ===================================================================== */
const APPS_SCRIPT_URL = "DUMMY_APPS_SCRIPT_WEB_APP_URL";
const SBI_COLLECT_URL = "https://sbcollect.sbi.bank.in/sbicollect/icollecthome.htm";
const PAYMENT_GUIDE_URL = "https://drive.google.com/file/d/1ObJJ4HhNQGb_-W7DW9i43JT4i0n5-1XT/view?usp=sharing";
const COURSE_CODE = "IITK/OOA/2026-27/034";
const GEV_POSTER_URL = "https://drive.google.com/file/d/1jQSP-nOZlkRg193qGrrGVTsuR4UowR-J/view?usp=sharing";
const EARLY_BIRD_DEADLINE = "2026-11-07";
const MAX_FILE_MB = 5;

/* Rates — exactly from sample file (IEEE / non-IEEE, early / standard) */
const FEES = {
    "Indian Author (Industry) – Single Paper": { cur: "₹", early: [8850, 10620], std: [10620, 12390], type: "author", papers: 1, region: "Indian" },
    "Indian Author (Industry) – Two Papers": { cur: "₹", early: [11850, 13620], std: [13620, 15390], type: "author", papers: 2, region: "Indian" },
    "Indian Author (Academia) – Single Paper": { cur: "₹", early: [5900, 7080], std: [7080, 8260], type: "author", papers: 1, region: "Indian" },
    "Indian Author (Academia) – Two Papers": { cur: "₹", early: [8900, 10080], std: [10080, 11260], type: "author", papers: 2, region: "Indian" },
    "Indian Professional Attendee": { cur: "₹", early: [4130, 4720], std: [4720, 5310], type: "attendee", papers: 0, region: "Indian" },
    "Indian Student Attendee": { cur: "₹", early: [1000, 1250], std: [1250, 1500], type: "attendee", papers: 0, region: "Indian" },
    "Foreign Author (Academia / Industry) – Single Paper": { cur: "$", early: [210, 240], std: [240, 270], type: "author", papers: 1, region: "Foreign" },
    "Foreign Author (Academia / Industry) – Two Papers": { cur: "$", early: [240, 270], std: [270, 300], type: "author", papers: 2, region: "Foreign" },
    "Foreign Attendee": { cur: "$", early: [150, 180], std: [180, 210], type: "attendee", papers: 0, region: "Foreign" },
};
const PAGE_CHARGE = { Indian: 1000, Foreign: 25 };

const EMPTY = {
    name: "", email: "", mobile: "", institution: "",
    role: "", papers: "1",
    paperId1: "", paperTitle1: "", pages1: "6",
    paperId2: "", paperTitle2: "", pages2: "6",
    region: "", ieee: "no", ieeeNumber: "", category: "",
    hostel: "no", hostelType: "", hostelDays: "", checkIn: "", checkOut: "",
    sbiRef: "", payDate: "", receipt: null, ieeeCard: null,
};

const isEarlyBird = () => new Date().toISOString().slice(0, 10) <= EARLY_BIRD_DEADLINE;

const fileToBase64 = (file) =>
    new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
    });

const RegistrationForm = () => {
    const [step, setStep] = useState(1);
    const [f, setF] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [serverError, setServerError] = useState("");

    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

    /* Categories filtered by role + region + paper count */
    const categories = useMemo(() => {
        if (!f.role || !f.region) return [];
        return Object.keys(FEES).filter((k) => {
            const c = FEES[k];
            if (c.region !== f.region || c.type !== f.role) return false;
            if (f.role === "author") return c.papers === Number(f.papers);
            return true;
        });
    }, [f.role, f.region, f.papers]);

    /* Fee calculation */
    const fee = useMemo(() => {
        const c = FEES[f.category];
        if (!c) return null;
        const early = isEarlyBird();
        const tier = early ? c.early : c.std;
        const base = tier[f.ieee === "yes" ? 0 : 1];
        let extraPages = 0;
        if (c.type === "author") {
            extraPages += Math.max(0, Number(f.pages1) - 6);
            if (c.papers === 2) extraPages += Math.max(0, Number(f.pages2) - 6);
        }
        const extra = extraPages * PAGE_CHARGE[c.region];
        return { cur: c.cur, base, extraPages, extra, total: base + extra, early };
    }, [f.category, f.ieee, f.pages1, f.pages2]);

    const fmt = (n) => (fee?.cur === "₹" ? n.toLocaleString("en-IN") : n.toFixed(2));

    /* Validation per step */
    const validate = (s) => {
        const e = {};
        if (s === 1) {
            if (!f.name.trim()) e.name = "Required";
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Valid email required";
            if (!f.mobile.trim()) e.mobile = "Required";
            if (!f.institution.trim()) e.institution = "Required";
            if (!f.role) e.role = "Select one";
            if (f.role === "author") {
                if (!f.paperId1.trim()) e.paperId1 = "Required";
                if (!f.paperTitle1.trim()) e.paperTitle1 = "Required";
                if (f.papers === "2") {
                    if (!f.paperId2.trim()) e.paperId2 = "Required";
                    if (!f.paperTitle2.trim()) e.paperTitle2 = "Required";
                }
            }
        }
        if (s === 2) {
            if (!f.region) e.region = "Select one";
            if (f.ieee === "yes" && !f.ieeeNumber.trim()) e.ieeeNumber = "Required";
            if (!f.category) e.category = "Select a category";
            if (f.hostel === "yes") {
                if (!f.hostelType) e.hostelType = "Required";
                if (!f.hostelDays) e.hostelDays = "Required";
                if (!f.checkIn) e.checkIn = "Required";
                if (!f.checkOut) e.checkOut = "Required";
            }
        }
        if (s === 3) {
            if (!f.sbiRef.trim()) e.sbiRef = "Required";
            if (!f.payDate) e.payDate = "Required";
            if (!f.receipt) e.receipt = "Receipt PDF required";
            if (f.ieee === "yes" && !f.ieeeCard) e.ieeeCard = "IEEE card required";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const next = () => validate(step) && setStep(step + 1);
    const back = () => setStep(step - 1);

    const onFile = (k) => (ev) => {
        const file = ev.target.files[0];
        if (!file) return set(k, null);
        if (file.type !== "application/pdf") return setErrors((p) => ({ ...p, [k]: "PDF only" }));
        if (file.size > MAX_FILE_MB * 1024 * 1024) return setErrors((p) => ({ ...p, [k]: `Max ${MAX_FILE_MB} MB` }));
        setErrors((p) => ({ ...p, [k]: undefined }));
        set(k, file);
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (!validate(3)) return;
        setSubmitting(true);
        setServerError("");
        try {
            const safeName = f.name.trim().replace(/\s+/g, "_");
            const receiptName = f.role === "author" ? `Receipt_${safeName}_${f.paperId1}.pdf` : `Receipt_${safeName}.pdf`;
            const payload = {
                ...f,
                receipt: undefined,
                ieeeCard: undefined,
                feeTier: fee.early ? "Early Bird" : "Standard",
                feeBase: fee.base,
                extraPages: fee.extraPages,
                extraCharge: fee.extra,
                feeTotal: fee.total,
                currency: fee.cur,
                courseCode: COURSE_CODE,
                receiptFile: { name: receiptName, data: await fileToBase64(f.receipt) },
                ieeeCardFile: f.ieeeCard ? { name: `IEEE_${safeName}.pdf`, data: await fileToBase64(f.ieeeCard) } : null,
            };
            const res = await fetch(APPS_SCRIPT_URL, {
                method: "POST",
                headers: { "Content-Type": "text/plain;charset=utf-8" },
                body: JSON.stringify(payload),
            });
            const json = await res.json();
            if (!json.ok) throw new Error(json.error || "Submission failed");
            setDone(true);
        } catch (err) {
            setServerError(err.message || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const Err = ({ k }) => (errors[k] ? <span className="rf-err">{errors[k]}</span> : null);

    if (done) {
        return (
            <div className="rf-page">
                <div className="rf-card rf-success">
                    <h2>Registration Submitted</h2>
                    <p>Thank you, <strong>{f.name}</strong>. A confirmation email has been sent to <strong>{f.email}</strong>.</p>
                    <p>For queries: <a href="mailto:sanket@ecovillage.org.in">sanket@ecovillage.org.in</a></p>
                </div>
            </div>
        );
    }

    return (
        <div className="rf-page">
            <div className="rf-card">
                <header className="rf-head">
                    <h1>SANKET 2026 – Conference Registration</h1>
                    <p>4th &amp; 5th December 2026 · IIT Bombay, Mumbai</p>
                    <div className="rf-steps">
                        {["Participant", "Category & Fee", "Payment"].map((l, i) => (
                            <span key={l} className={step === i + 1 ? "active" : step > i + 1 ? "done" : ""}>{i + 1}. {l}</span>
                        ))}
                    </div>
                </header>

                <form onSubmit={submit} noValidate>
                    {/* ================= STEP 1 ================= */}
                    {step === 1 && (
                        <section className="rf-section">
                            <h2>Participant Details</h2>
                            <div className="rf-grid">
                                <label>Full Name *<input value={f.name} onChange={(e) => set("name", e.target.value)} /><Err k="name" /></label>
                                <label>Email ID *<input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /><Err k="email" /></label>
                                <label>Mobile Number *<input value={f.mobile} onChange={(e) => set("mobile", e.target.value)} /><Err k="mobile" /></label>
                                <label>Institution / Organisation *<input value={f.institution} onChange={(e) => set("institution", e.target.value)} /><Err k="institution" /></label>
                            </div>

                            <fieldset className="rf-radio">
                                <legend>I am registering as *</legend>
                                <label><input type="radio" checked={f.role === "author"} onChange={() => { set("role", "author"); set("category", ""); }} /> Author (presenting paper)</label>
                                <label><input type="radio" checked={f.role === "attendee"} onChange={() => { set("role", "attendee"); set("category", ""); }} /> Attendee (Student / Professional)</label>
                                <Err k="role" />
                            </fieldset>

                            {f.role === "author" && (
                                <>
                                    <fieldset className="rf-radio">
                                        <legend>Number of papers *</legend>
                                        <label><input type="radio" checked={f.papers === "1"} onChange={() => { set("papers", "1"); set("category", ""); }} /> 1 paper</label>
                                        <label><input type="radio" checked={f.papers === "2"} onChange={() => { set("papers", "2"); set("category", ""); }} /> 2 papers</label>
                                    </fieldset>
                                    <p className="rf-note">Authors presenting more than 2 papers must complete multiple registrations.</p>

                                    <div className="rf-paper">
                                        <h3>Paper 1</h3>
                                        <div className="rf-grid">
                                            <label>Paper ID *<input value={f.paperId1} onChange={(e) => set("paperId1", e.target.value)} /><Err k="paperId1" /></label>
                                            <label>Number of pages *
                                                <select value={f.pages1} onChange={(e) => set("pages1", e.target.value)}>
                                                    <option value="6">Up to 6 pages</option>
                                                    <option value="7">7 pages</option>
                                                    <option value="8">8 pages</option>
                                                </select>
                                            </label>
                                            <label className="rf-span">Paper Title *<input value={f.paperTitle1} onChange={(e) => set("paperTitle1", e.target.value)} /><Err k="paperTitle1" /></label>
                                        </div>
                                    </div>

                                    {f.papers === "2" && (
                                        <div className="rf-paper">
                                            <h3>Paper 2</h3>
                                            <div className="rf-grid">
                                                <label>Paper ID *<input value={f.paperId2} onChange={(e) => set("paperId2", e.target.value)} /><Err k="paperId2" /></label>
                                                <label>Number of pages *
                                                    <select value={f.pages2} onChange={(e) => set("pages2", e.target.value)}>
                                                        <option value="6">Up to 6 pages</option>
                                                        <option value="7">7 pages</option>
                                                        <option value="8">8 pages</option>
                                                    </select>
                                                </label>
                                                <label className="rf-span">Paper Title *<input value={f.paperTitle2} onChange={(e) => set("paperTitle2", e.target.value)} /><Err k="paperTitle2" /></label>
                                            </div>
                                        </div>
                                    )}
                                    <p className="rf-note"><strong>Papers exceeding 8 pages are not permitted.</strong> Pages beyond 6 incur additional page charges (₹1,000 / $25 per page).</p>
                                </>
                            )}

                            <div className="rf-actions">
                                <button type="button" className="rf-btn primary" onClick={next}>Next →</button>
                            </div>
                        </section>
                    )}

                    {/* ================= STEP 2 ================= */}
                    {step === 2 && (
                        <section className="rf-section">
                            <h2>Category &amp; Fee</h2>

                            <fieldset className="rf-radio">
                                <legend>Nationality *</legend>
                                <label><input type="radio" checked={f.region === "Indian"} onChange={() => { set("region", "Indian"); set("category", ""); }} /> Indian</label>
                                <label><input type="radio" checked={f.region === "Foreign"} onChange={() => { set("region", "Foreign"); set("category", ""); }} /> Foreign</label>
                                <Err k="region" />
                            </fieldset>

                            <fieldset className="rf-radio">
                                <legend>IEEE Member? *</legend>
                                <label><input type="radio" checked={f.ieee === "yes"} onChange={() => set("ieee", "yes")} /> Yes</label>
                                <label><input type="radio" checked={f.ieee === "no"} onChange={() => { set("ieee", "no"); set("ieeeNumber", ""); set("ieeeCard", null); }} /> No</label>
                            </fieldset>
                            {f.ieee === "yes" && (
                                <label className="rf-single">IEEE Membership Number *<input value={f.ieeeNumber} onChange={(e) => set("ieeeNumber", e.target.value)} /><Err k="ieeeNumber" /></label>
                            )}

                            <label className="rf-single">Registration Category *
                                <select value={f.category} onChange={(e) => set("category", e.target.value)} disabled={!categories.length}>
                                    <option value="">{categories.length ? "— Select —" : "Select role & nationality first"}</option>
                                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <Err k="category" />
                            </label>

                            {fee && (
                                <div className="rf-fee">
                                    <div><span>Tier</span><strong>{fee.early ? "Early Bird" : "Standard"} · {f.ieee === "yes" ? "IEEE Member" : "Non-IEEE"}</strong></div>
                                    <div><span>Registration Fee</span><strong>{fee.cur}{fmt(fee.base)}</strong></div>
                                    {fee.extraPages > 0 && (
                                        <div><span>Additional pages ({fee.extraPages} × {fee.cur}{fmt(PAGE_CHARGE[f.region])})</span><strong>{fee.cur}{fmt(fee.extra)}</strong></div>
                                    )}
                                    <div className="rf-total"><span>Total Payable</span><strong>{fee.cur}{fmt(fee.total)}</strong></div>
                                    <p className="rf-note">Inclusive of 18% GST. SBI Collect gateway charges are extra.</p>
                                </div>
                            )}

                            <fieldset className="rf-radio">
                                <legend>IIT Bombay Hostel Accommodation Request (request only — no charge now)</legend>
                                <label><input type="radio" checked={f.hostel === "yes"} onChange={() => set("hostel", "yes")} /> Yes, I request accommodation</label>
                                <label><input type="radio" checked={f.hostel === "no"} onChange={() => set("hostel", "no")} /> No</label>
                            </fieldset>
                            <div className="rf-disclaimer">
                                <strong>Disclaimer:</strong> Hostel accommodation is not guaranteed and is subject to availability.
                                Several other academic events, placements and admissions take place on campus in early December, so
                                hostel availability is uncertain. Participants who request accommodation will be informed closer to the conference dates.
                            </div>
                            {f.hostel === "yes" && (
                                <div className="rf-grid">
                                    <label>Room Type *
                                        <select value={f.hostelType} onChange={(e) => set("hostelType", e.target.value)}>
                                            <option value="">— Select —</option>
                                            <option>Single</option>
                                            <option>Shared</option>
                                            <option>Guest House</option>
                                        </select><Err k="hostelType" />
                                    </label>
                                    <label>Number of Days *<input type="number" min="1" value={f.hostelDays} onChange={(e) => set("hostelDays", e.target.value)} /><Err k="hostelDays" /></label>
                                    <label>Check-in *<input type="datetime-local" value={f.checkIn} onChange={(e) => set("checkIn", e.target.value)} /><Err k="checkIn" /></label>
                                    <label>Check-out *<input type="datetime-local" value={f.checkOut} onChange={(e) => set("checkOut", e.target.value)} /><Err k="checkOut" /></label>
                                </div>
                            )}

                            <div className="rf-gev">
                                <div>
                                    <strong>Govardhan Eco Village visit · 6 December 2026</strong>
                                    <p>Optional Eco-Discovery Tour after the conference. Download the invitation &amp; itinerary.</p>
                                </div>
                                <a className="rf-btn secondary" href={GEV_POSTER_URL} target="_blank" rel="noopener noreferrer">GEV Visit Poster ↗</a>
                            </div>

                            <div className="rf-actions">
                                <button type="button" className="rf-btn secondary" onClick={back}>← Back</button>
                                <button type="button" className="rf-btn primary" onClick={next}>Next →</button>
                            </div>
                        </section>
                    )}

                    {/* ================= STEP 3 ================= */}
                    {step === 3 && fee && (
                        <section className="rf-section">
                            <h2>Payment</h2>

                            <div className="rf-fee">
                                <div className="rf-total"><span>Amount to pay on SBI Collect</span><strong>{fee.cur}{fmt(fee.total)}</strong></div>
                                <div><span>Course Code</span><strong>{COURSE_CODE}</strong></div>
                            </div>

                            <ol className="rf-guide">
                                <li>Open SBI Collect → <strong>Educational Institutions</strong>.</li>
                                <li>Search and select <strong>Indian Institute of Technology Kanpur</strong>.</li>
                                <li>Payment Category: <strong>Outreach Activities (OOA)</strong>.</li>
                                <li>Course Code <strong>{COURSE_CODE}</strong>, Title: Sustainability through Ancient Knowledge and Emerging Technologies (SANKET), Duration 04.12.2026 – 05.12.2026, Course Fee: <strong>{fee.cur}{fmt(fee.total)}</strong>.</li>
                                <li>Enter your Name, Email, Mobile; pay; download the receipt.</li>
                            </ol>
                            <div className="rf-ext-row">
                                <a className="rf-btn primary rf-ext" href={SBI_COLLECT_URL} target="_blank" rel="noopener noreferrer">Open SBI Collect ↗</a>
                                <a className="rf-btn secondary rf-ext" href={PAYMENT_GUIDE_URL} target="_blank" rel="noopener noreferrer">Payment Guide (PDF) ↗</a>
                            </div>

                            <div className="rf-grid">
                                <label>SBI Collect Reference Number *<input value={f.sbiRef} onChange={(e) => set("sbiRef", e.target.value)} /><Err k="sbiRef" /></label>
                                <label>Date of Payment *<input type="date" value={f.payDate} onChange={(e) => set("payDate", e.target.value)} /><Err k="payDate" /></label>
                                <label className="rf-span">Payment Receipt (PDF, max {MAX_FILE_MB} MB) *
                                    <input type="file" accept="application/pdf" onChange={onFile("receipt")} />
                                    <small>Will be saved as {f.role === "author" ? "Receipt_<name>_<PaperID>.pdf" : "Receipt_<name>.pdf"}</small>
                                    <Err k="receipt" />
                                </label>
                                {f.ieee === "yes" && (
                                    <label className="rf-span">IEEE Membership Card (PDF) *
                                        <input type="file" accept="application/pdf" onChange={onFile("ieeeCard")} />
                                        <Err k="ieeeCard" />
                                    </label>
                                )}
                            </div>

                            {serverError && <p className="rf-err rf-server">{serverError}</p>}

                            <div className="rf-actions">
                                <button type="button" className="rf-btn secondary" onClick={back} disabled={submitting}>← Back</button>
                                <button type="submit" className="rf-btn primary" disabled={submitting}>{submitting ? "Submitting…" : "Submit Registration"}</button>
                            </div>
                        </section>
                    )}
                </form>
            </div>
        </div>
    );
};

export default RegistrationForm;


// import { useState, useMemo } from "react";
// import "../../stylesheets2025/registrationForm.css";

// /* =====================================================================
//    CONFIG — DUMMY links, baad me real se replace karna
//    ===================================================================== */
// const APPS_SCRIPT_URL = "DUMMY_APPS_SCRIPT_WEB_APP_URL";
// const SBI_COLLECT_URL = "https://sbcollect.sbi.bank.in/sbicollect/icollecthome.htm";
// const PAYMENT_GUIDE_URL = "https://drive.google.com/file/d/1ObJJ4HhNQGb_-W7DW9i43JT4i0n5-1XT/view?usp=sharing";
// const COURSE_CODE = "IITK/OOA/2026-27/034";
// const EARLY_BIRD_DEADLINE = "2026-11-07";
// const MAX_FILE_MB = 5;

// /* Rates — exactly from sample file (IEEE / non-IEEE, early / standard) */
// const FEES = {
//     "Indian Author (Industry) – Single Paper": { cur: "₹", early: [8850, 10620], std: [10620, 12390], type: "author", papers: 1, region: "Indian" },
//     "Indian Author (Industry) – Two Papers": { cur: "₹", early: [11850, 13620], std: [13620, 15390], type: "author", papers: 2, region: "Indian" },
//     "Indian Author (Academia) – Single Paper": { cur: "₹", early: [5900, 7080], std: [7080, 8260], type: "author", papers: 1, region: "Indian" },
//     "Indian Author (Academia) – Two Papers": { cur: "₹", early: [8900, 10080], std: [10080, 11260], type: "author", papers: 2, region: "Indian" },
//     "Indian Professional Attendee": { cur: "₹", early: [4130, 4720], std: [4720, 5310], type: "attendee", papers: 0, region: "Indian" },
//     "Indian Student Attendee": { cur: "₹", early: [1000, 1250], std: [1250, 1500], type: "attendee", papers: 0, region: "Indian" },
//     "Foreign Author (Academia / Industry) – Single Paper": { cur: "$", early: [210, 240], std: [240, 270], type: "author", papers: 1, region: "Foreign" },
//     "Foreign Author (Academia / Industry) – Two Papers": { cur: "$", early: [240, 270], std: [270, 300], type: "author", papers: 2, region: "Foreign" },
//     "Foreign Attendee": { cur: "$", early: [150, 180], std: [180, 210], type: "attendee", papers: 0, region: "Foreign" },
// };
// const PAGE_CHARGE = { Indian: 1000, Foreign: 25 };

// const EMPTY = {
//     name: "", email: "", mobile: "", institution: "",
//     role: "", papers: "1",
//     paperId1: "", paperTitle1: "", pages1: "6",
//     paperId2: "", paperTitle2: "", pages2: "6",
//     region: "", ieee: "no", ieeeNumber: "", category: "",
//     hostel: "no", hostelType: "", hostelDays: "", checkIn: "", checkOut: "",
//     sbiRef: "", payDate: "", receipt: null, ieeeCard: null,
// };

// const isEarlyBird = () => new Date().toISOString().slice(0, 10) <= EARLY_BIRD_DEADLINE;

// const fileToBase64 = (file) =>
//     new Promise((res, rej) => {
//         const r = new FileReader();
//         r.onload = () => res(r.result.split(",")[1]);
//         r.onerror = rej;
//         r.readAsDataURL(file);
//     });

// const RegistrationForm = () => {
//     const [step, setStep] = useState(1);
//     const [f, setF] = useState(EMPTY);
//     const [errors, setErrors] = useState({});
//     const [submitting, setSubmitting] = useState(false);
//     const [done, setDone] = useState(false);
//     const [serverError, setServerError] = useState("");

//     const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

//     /* Categories filtered by role + region + paper count */
//     const categories = useMemo(() => {
//         if (!f.role || !f.region) return [];
//         return Object.keys(FEES).filter((k) => {
//             const c = FEES[k];
//             if (c.region !== f.region || c.type !== f.role) return false;
//             if (f.role === "author") return c.papers === Number(f.papers);
//             return true;
//         });
//     }, [f.role, f.region, f.papers]);

//     /* Fee calculation */
//     const fee = useMemo(() => {
//         const c = FEES[f.category];
//         if (!c) return null;
//         const early = isEarlyBird();
//         const tier = early ? c.early : c.std;
//         const base = tier[f.ieee === "yes" ? 0 : 1];
//         let extraPages = 0;
//         if (c.type === "author") {
//             extraPages += Math.max(0, Number(f.pages1) - 6);
//             if (c.papers === 2) extraPages += Math.max(0, Number(f.pages2) - 6);
//         }
//         const extra = extraPages * PAGE_CHARGE[c.region];
//         return { cur: c.cur, base, extraPages, extra, total: base + extra, early };
//     }, [f.category, f.ieee, f.pages1, f.pages2]);

//     const fmt = (n) => (fee?.cur === "₹" ? n.toLocaleString("en-IN") : n.toFixed(2));

//     /* Validation per step */
//     const validate = (s) => {
//         const e = {};
//         if (s === 1) {
//             if (!f.name.trim()) e.name = "Required";
//             if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Valid email required";
//             if (!f.mobile.trim()) e.mobile = "Required";
//             if (!f.institution.trim()) e.institution = "Required";
//             if (!f.role) e.role = "Select one";
//             if (f.role === "author") {
//                 if (!f.paperId1.trim()) e.paperId1 = "Required";
//                 if (!f.paperTitle1.trim()) e.paperTitle1 = "Required";
//                 if (f.papers === "2") {
//                     if (!f.paperId2.trim()) e.paperId2 = "Required";
//                     if (!f.paperTitle2.trim()) e.paperTitle2 = "Required";
//                 }
//             }
//         }
//         if (s === 2) {
//             if (!f.region) e.region = "Select one";
//             if (f.ieee === "yes" && !f.ieeeNumber.trim()) e.ieeeNumber = "Required";
//             if (!f.category) e.category = "Select a category";
//             if (f.hostel === "yes") {
//                 if (!f.hostelType) e.hostelType = "Required";
//                 if (!f.hostelDays) e.hostelDays = "Required";
//                 if (!f.checkIn) e.checkIn = "Required";
//                 if (!f.checkOut) e.checkOut = "Required";
//             }
//         }
//         if (s === 3) {
//             if (!f.sbiRef.trim()) e.sbiRef = "Required";
//             if (!f.payDate) e.payDate = "Required";
//             if (!f.receipt) e.receipt = "Receipt PDF required";
//             if (f.ieee === "yes" && !f.ieeeCard) e.ieeeCard = "IEEE card required";
//         }
//         setErrors(e);
//         return Object.keys(e).length === 0;
//     };

//     const next = () => validate(step) && setStep(step + 1);
//     const back = () => setStep(step - 1);

//     const onFile = (k) => (ev) => {
//         const file = ev.target.files[0];
//         if (!file) return set(k, null);
//         if (file.type !== "application/pdf") return setErrors((p) => ({ ...p, [k]: "PDF only" }));
//         if (file.size > MAX_FILE_MB * 1024 * 1024) return setErrors((p) => ({ ...p, [k]: `Max ${MAX_FILE_MB} MB` }));
//         setErrors((p) => ({ ...p, [k]: undefined }));
//         set(k, file);
//     };

//     const submit = async (ev) => {
//         ev.preventDefault();
//         if (!validate(3)) return;
//         setSubmitting(true);
//         setServerError("");
//         try {
//             const safeName = f.name.trim().replace(/\s+/g, "_");
//             const receiptName = f.role === "author" ? `Receipt_${safeName}_${f.paperId1}.pdf` : `Receipt_${safeName}.pdf`;
//             const payload = {
//                 ...f,
//                 receipt: undefined,
//                 ieeeCard: undefined,
//                 feeTier: fee.early ? "Early Bird" : "Standard",
//                 feeBase: fee.base,
//                 extraPages: fee.extraPages,
//                 extraCharge: fee.extra,
//                 feeTotal: fee.total,
//                 currency: fee.cur,
//                 courseCode: COURSE_CODE,
//                 receiptFile: { name: receiptName, data: await fileToBase64(f.receipt) },
//                 ieeeCardFile: f.ieeeCard ? { name: `IEEE_${safeName}.pdf`, data: await fileToBase64(f.ieeeCard) } : null,
//             };
//             const res = await fetch(APPS_SCRIPT_URL, {
//                 method: "POST",
//                 headers: { "Content-Type": "text/plain;charset=utf-8" },
//                 body: JSON.stringify(payload),
//             });
//             const json = await res.json();
//             if (!json.ok) throw new Error(json.error || "Submission failed");
//             setDone(true);
//         } catch (err) {
//             setServerError(err.message || "Something went wrong. Please try again.");
//         } finally {
//             setSubmitting(false);
//         }
//     };

//     const Err = ({ k }) => (errors[k] ? <span className="rf-err">{errors[k]}</span> : null);

//     if (done) {
//         return (
//             <div className="rf-page">
//                 <div className="rf-card rf-success">
//                     <h2>Registration Submitted</h2>
//                     <p>Thank you, <strong>{f.name}</strong>. A confirmation email has been sent to <strong>{f.email}</strong>.</p>
//                     <p>For queries: <a href="mailto:sanket@ecovillage.org.in">sanket@ecovillage.org.in</a></p>
//                 </div>
//             </div>
//         );
//     }

//     return (
//         <div className="rf-page">
//             <div className="rf-card">
//                 <header className="rf-head">
//                     <h1>SANKET 2026 – Conference Registration</h1>
//                     <p>4th &amp; 5th December 2026 · IIT Bombay, Mumbai</p>
//                     <div className="rf-steps">
//                         {["Participant", "Category & Fee", "Payment"].map((l, i) => (
//                             <span key={l} className={step === i + 1 ? "active" : step > i + 1 ? "done" : ""}>{i + 1}. {l}</span>
//                         ))}
//                     </div>
//                 </header>

//                 <form onSubmit={submit} noValidate>
//                     {/* ================= STEP 1 ================= */}
//                     {step === 1 && (
//                         <section className="rf-section">
//                             <h2>Participant Details</h2>
//                             <div className="rf-grid">
//                                 <label>Full Name *<input value={f.name} onChange={(e) => set("name", e.target.value)} /><Err k="name" /></label>
//                                 <label>Email ID *<input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /><Err k="email" /></label>
//                                 <label>Mobile Number *<input value={f.mobile} onChange={(e) => set("mobile", e.target.value)} /><Err k="mobile" /></label>
//                                 <label>Institution / Organisation *<input value={f.institution} onChange={(e) => set("institution", e.target.value)} /><Err k="institution" /></label>
//                             </div>

//                             <fieldset className="rf-radio">
//                                 <legend>I am registering as *</legend>
//                                 <label><input type="radio" checked={f.role === "author"} onChange={() => { set("role", "author"); set("category", ""); }} /> Author (presenting paper)</label>
//                                 <label><input type="radio" checked={f.role === "attendee"} onChange={() => { set("role", "attendee"); set("category", ""); }} /> Attendee (Student / Professional)</label>
//                                 <Err k="role" />
//                             </fieldset>

//                             {f.role === "author" && (
//                                 <>
//                                     <fieldset className="rf-radio">
//                                         <legend>Number of papers *</legend>
//                                         <label><input type="radio" checked={f.papers === "1"} onChange={() => { set("papers", "1"); set("category", ""); }} /> 1 paper</label>
//                                         <label><input type="radio" checked={f.papers === "2"} onChange={() => { set("papers", "2"); set("category", ""); }} /> 2 papers</label>
//                                     </fieldset>
//                                     <p className="rf-note">Authors presenting more than 2 papers must complete multiple registrations.</p>

//                                     <div className="rf-paper">
//                                         <h3>Paper 1</h3>
//                                         <div className="rf-grid">
//                                             <label>Paper ID *<input value={f.paperId1} onChange={(e) => set("paperId1", e.target.value)} /><Err k="paperId1" /></label>
//                                             <label>Number of pages *
//                                                 <select value={f.pages1} onChange={(e) => set("pages1", e.target.value)}>
//                                                     <option value="6">Up to 6 pages</option>
//                                                     <option value="7">7 pages</option>
//                                                     <option value="8">8 pages</option>
//                                                 </select>
//                                             </label>
//                                             <label className="rf-span">Paper Title *<input value={f.paperTitle1} onChange={(e) => set("paperTitle1", e.target.value)} /><Err k="paperTitle1" /></label>
//                                         </div>
//                                     </div>

//                                     {f.papers === "2" && (
//                                         <div className="rf-paper">
//                                             <h3>Paper 2</h3>
//                                             <div className="rf-grid">
//                                                 <label>Paper ID *<input value={f.paperId2} onChange={(e) => set("paperId2", e.target.value)} /><Err k="paperId2" /></label>
//                                                 <label>Number of pages *
//                                                     <select value={f.pages2} onChange={(e) => set("pages2", e.target.value)}>
//                                                         <option value="6">Up to 6 pages</option>
//                                                         <option value="7">7 pages</option>
//                                                         <option value="8">8 pages</option>
//                                                     </select>
//                                                 </label>
//                                                 <label className="rf-span">Paper Title *<input value={f.paperTitle2} onChange={(e) => set("paperTitle2", e.target.value)} /><Err k="paperTitle2" /></label>
//                                             </div>
//                                         </div>
//                                     )}
//                                     <p className="rf-note"><strong>Papers exceeding 8 pages are not permitted.</strong> Pages beyond 6 incur additional page charges (₹1,000 / $25 per page).</p>
//                                 </>
//                             )}

//                             <div className="rf-actions">
//                                 <button type="button" className="rf-btn primary" onClick={next}>Next →</button>
//                             </div>
//                         </section>
//                     )}

//                     {/* ================= STEP 2 ================= */}
//                     {step === 2 && (
//                         <section className="rf-section">
//                             <h2>Category &amp; Fee</h2>

//                             <fieldset className="rf-radio">
//                                 <legend>Nationality *</legend>
//                                 <label><input type="radio" checked={f.region === "Indian"} onChange={() => { set("region", "Indian"); set("category", ""); }} /> Indian</label>
//                                 <label><input type="radio" checked={f.region === "Foreign"} onChange={() => { set("region", "Foreign"); set("category", ""); }} /> Foreign</label>
//                                 <Err k="region" />
//                             </fieldset>

//                             <fieldset className="rf-radio">
//                                 <legend>IEEE Member? *</legend>
//                                 <label><input type="radio" checked={f.ieee === "yes"} onChange={() => set("ieee", "yes")} /> Yes</label>
//                                 <label><input type="radio" checked={f.ieee === "no"} onChange={() => { set("ieee", "no"); set("ieeeNumber", ""); set("ieeeCard", null); }} /> No</label>
//                             </fieldset>
//                             {f.ieee === "yes" && (
//                                 <label className="rf-single">IEEE Membership Number *<input value={f.ieeeNumber} onChange={(e) => set("ieeeNumber", e.target.value)} /><Err k="ieeeNumber" /></label>
//                             )}

//                             <label className="rf-single">Registration Category *
//                                 <select value={f.category} onChange={(e) => set("category", e.target.value)} disabled={!categories.length}>
//                                     <option value="">{categories.length ? "— Select —" : "Select role & nationality first"}</option>
//                                     {categories.map((c) => <option key={c} value={c}>{c}</option>)}
//                                 </select>
//                                 <Err k="category" />
//                             </label>

//                             {fee && (
//                                 <div className="rf-fee">
//                                     <div><span>Tier</span><strong>{fee.early ? "Early Bird" : "Standard"} · {f.ieee === "yes" ? "IEEE Member" : "Non-IEEE"}</strong></div>
//                                     <div><span>Registration Fee</span><strong>{fee.cur}{fmt(fee.base)}</strong></div>
//                                     {fee.extraPages > 0 && (
//                                         <div><span>Additional pages ({fee.extraPages} × {fee.cur}{fmt(PAGE_CHARGE[f.region])})</span><strong>{fee.cur}{fmt(fee.extra)}</strong></div>
//                                     )}
//                                     <div className="rf-total"><span>Total Payable</span><strong>{fee.cur}{fmt(fee.total)}</strong></div>
//                                     <p className="rf-note">Inclusive of 18% GST. SBI Collect gateway charges are extra.</p>
//                                 </div>
//                             )}

//                             <fieldset className="rf-radio">
//                                 <legend>IIT Bombay Hostel Accommodation Request (request only — no charge now)</legend>
//                                 <label><input type="radio" checked={f.hostel === "yes"} onChange={() => set("hostel", "yes")} /> Yes, I request accommodation</label>
//                                 <label><input type="radio" checked={f.hostel === "no"} onChange={() => set("hostel", "no")} /> No</label>
//                             </fieldset>
//                             {f.hostel === "yes" && (
//                                 <div className="rf-grid">
//                                     <label>Room Type *
//                                         <select value={f.hostelType} onChange={(e) => set("hostelType", e.target.value)}>
//                                             <option value="">— Select —</option>
//                                             <option>Single</option>
//                                             <option>Shared</option>
//                                             <option>Guest House</option>
//                                         </select><Err k="hostelType" />
//                                     </label>
//                                     <label>Number of Days *<input type="number" min="1" value={f.hostelDays} onChange={(e) => set("hostelDays", e.target.value)} /><Err k="hostelDays" /></label>
//                                     <label>Check-in *<input type="datetime-local" value={f.checkIn} onChange={(e) => set("checkIn", e.target.value)} /><Err k="checkIn" /></label>
//                                     <label>Check-out *<input type="datetime-local" value={f.checkOut} onChange={(e) => set("checkOut", e.target.value)} /><Err k="checkOut" /></label>
//                                 </div>
//                             )}

//                             <div className="rf-actions">
//                                 <button type="button" className="rf-btn secondary" onClick={back}>← Back</button>
//                                 <button type="button" className="rf-btn primary" onClick={next}>Next →</button>
//                             </div>
//                         </section>
//                     )}

//                     {/* ================= STEP 3 ================= */}
//                     {step === 3 && fee && (
//                         <section className="rf-section">
//                             <h2>Payment</h2>

//                             <div className="rf-fee">
//                                 <div className="rf-total"><span>Amount to pay on SBI Collect</span><strong>{fee.cur}{fmt(fee.total)}</strong></div>
//                                 <div><span>Course Code</span><strong>{COURSE_CODE}</strong></div>
//                             </div>

//                             <ol className="rf-guide">
//                                 <li>Open SBI Collect → <strong>Educational Institutions</strong>.</li>
//                                 <li>Search and select <strong>Indian Institute of Technology Kanpur</strong>.</li>
//                                 <li>Payment Category: <strong>Outreach Activities (OOA)</strong>.</li>
//                                 <li>Course Code <strong>{COURSE_CODE}</strong>, Title: Sustainability through Ancient Knowledge and Emerging Technologies (SANKET), Duration 04.12.2026 – 05.12.2026, Course Fee: <strong>{fee.cur}{fmt(fee.total)}</strong>.</li>
//                                 <li>Enter your Name, Email, Mobile; pay; download the receipt.</li>
//                             </ol>
//                             <div className="rf-ext-row">
//                                 <a className="rf-btn primary rf-ext" href={SBI_COLLECT_URL} target="_blank" rel="noopener noreferrer">Open SBI Collect ↗</a>
//                                 <a className="rf-btn secondary rf-ext" href={PAYMENT_GUIDE_URL} target="_blank" rel="noopener noreferrer">Payment Guide (PDF) ↗</a>
//                             </div>

//                             <div className="rf-grid">
//                                 <label>SBI Collect Reference Number *<input value={f.sbiRef} onChange={(e) => set("sbiRef", e.target.value)} /><Err k="sbiRef" /></label>
//                                 <label>Date of Payment *<input type="date" value={f.payDate} onChange={(e) => set("payDate", e.target.value)} /><Err k="payDate" /></label>
//                                 <label className="rf-span">Payment Receipt (PDF, max {MAX_FILE_MB} MB) *
//                                     <input type="file" accept="application/pdf" onChange={onFile("receipt")} />
//                                     <small>Will be saved as {f.role === "author" ? "Receipt_<name>_<PaperID>.pdf" : "Receipt_<name>.pdf"}</small>
//                                     <Err k="receipt" />
//                                 </label>
//                                 {f.ieee === "yes" && (
//                                     <label className="rf-span">IEEE Membership Card (PDF) *
//                                         <input type="file" accept="application/pdf" onChange={onFile("ieeeCard")} />
//                                         <Err k="ieeeCard" />
//                                     </label>
//                                 )}
//                             </div>

//                             {serverError && <p className="rf-err rf-server">{serverError}</p>}

//                             <div className="rf-actions">
//                                 <button type="button" className="rf-btn secondary" onClick={back} disabled={submitting}>← Back</button>
//                                 <button type="submit" className="rf-btn primary" disabled={submitting}>{submitting ? "Submitting…" : "Submit Registration"}</button>
//                             </div>
//                         </section>
//                     )}
//                 </form>
//             </div>
//         </div>
//     );
// };

// export default RegistrationForm;


// import { useState, useMemo } from "react";
// import "../../stylesheets2025/registrationForm.css";

// /* =====================================================================
//    CONFIG — DUMMY links, baad me real se replace karna
//    ===================================================================== */
// const APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbylCJPwOlJZapHTl2EVKvzBtCZW-Y1yMMgyouGrYxaDknXvyk4HYXLab2DeVMgWFaoF9A/exec";
// const SBI_COLLECT_URL = "https://sbcollect.sbi.bank.in/sbicollect/icollecthome.htm";
// const PAYMENT_GUIDE_URL = "https://drive.google.com/file/d/1ObJJ4HhNQGb_-W7DW9i43JT4i0n5-1XT/view?usp=sharing";
// const COURSE_CODE = "IITK/OOA/2026-27/034";
// const EARLY_BIRD_DEADLINE = "2026-11-07";
// const MAX_FILE_MB = 5;

// /* Rates — exactly from sample file (IEEE / non-IEEE, early / standard) */
// const FEES = {
//     "Indian Author (Industry) – Single Paper": { cur: "₹", early: [8850, 10620], std: [10620, 12390], type: "author", papers: 1, region: "Indian" },
//     "Indian Author (Industry) – Two Papers": { cur: "₹", early: [11850, 13620], std: [13620, 15390], type: "author", papers: 2, region: "Indian" },
//     "Indian Author (Academia) – Single Paper": { cur: "₹", early: [5900, 7080], std: [7080, 8260], type: "author", papers: 1, region: "Indian" },
//     "Indian Author (Academia) – Two Papers": { cur: "₹", early: [8900, 10080], std: [10080, 11260], type: "author", papers: 2, region: "Indian" },
//     "Indian Professional Attendee": { cur: "₹", early: [4130, 4720], std: [4720, 5310], type: "attendee", papers: 0, region: "Indian" },
//     "Indian Student Attendee": { cur: "₹", early: [1000, 1250], std: [1250, 1500], type: "attendee", papers: 0, region: "Indian" },
//     "Foreign Author (Academia / Industry) – Single Paper": { cur: "$", early: [210, 240], std: [240, 270], type: "author", papers: 1, region: "Foreign" },
//     "Foreign Author (Academia / Industry) – Two Papers": { cur: "$", early: [240, 270], std: [270, 300], type: "author", papers: 2, region: "Foreign" },
//     "Foreign Attendee": { cur: "$", early: [150, 180], std: [180, 210], type: "attendee", papers: 0, region: "Foreign" },
// };
// const PAGE_CHARGE = { Indian: 1000, Foreign: 25 };
// const GEV_VISIT_CHARGE = { Indian: 1200, Foreign: 15 }; // ₹1,200 / $15 — 6 Dec 2026 GEV visit
// const GEV_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScNnrpyNvl_CTP5O74_dA62dehjwGTOcdwvhfYJUAqrWS-zEw/viewform";

// const EMPTY = {
//     name: "", email: "", mobile: "", institution: "",
//     role: "", papers: "1",
//     paperId1: "", paperTitle1: "", pages1: "6",
//     paperId2: "", paperTitle2: "", pages2: "6",
//     region: "", ieee: "no", ieeeNumber: "", category: "",
//     hostel: "no", hostelType: "", hostelDays: "", checkIn: "", checkOut: "",
//     gev: "no",
//     sbiRef: "", payDate: "", receipt: null, ieeeCard: null,
// };

// const isEarlyBird = () => new Date().toISOString().slice(0, 10) <= EARLY_BIRD_DEADLINE;

// const fileToBase64 = (file) =>
//     new Promise((res, rej) => {
//         const r = new FileReader();
//         r.onload = () => res(r.result.split(",")[1]);
//         r.onerror = rej;
//         r.readAsDataURL(file);
//     });

// const RegistrationForm = () => {
//     const [step, setStep] = useState(1);
//     const [f, setF] = useState(EMPTY);
//     const [errors, setErrors] = useState({});
//     const [submitting, setSubmitting] = useState(false);
//     const [done, setDone] = useState(false);
//     const [serverError, setServerError] = useState("");

//     const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

//     /* Categories filtered by role + region + paper count */
//     const categories = useMemo(() => {
//         if (!f.role || !f.region) return [];
//         return Object.keys(FEES).filter((k) => {
//             const c = FEES[k];
//             if (c.region !== f.region || c.type !== f.role) return false;
//             if (f.role === "author") return c.papers === Number(f.papers);
//             return true;
//         });
//     }, [f.role, f.region, f.papers]);

//     /* Fee calculation */
//     const fee = useMemo(() => {
//         const c = FEES[f.category];
//         if (!c) return null;
//         const early = isEarlyBird();
//         const tier = early ? c.early : c.std;
//         const base = tier[f.ieee === "yes" ? 0 : 1];
//         let extraPages = 0;
//         if (c.type === "author") {
//             extraPages += Math.max(0, Number(f.pages1) - 6);
//             if (c.papers === 2) extraPages += Math.max(0, Number(f.pages2) - 6);
//         }
//         const extra = extraPages * PAGE_CHARGE[c.region];
//         const gev = f.gev === "yes" ? GEV_VISIT_CHARGE[c.region] : 0;
//         return { cur: c.cur, base, extraPages, extra, gev, total: base + extra + gev, early };
//     }, [f.category, f.ieee, f.pages1, f.pages2, f.gev]);

//     const fmt = (n) => (fee?.cur === "₹" ? n.toLocaleString("en-IN") : n.toFixed(2));

//     /* Validation per step */
//     const validate = (s) => {
//         const e = {};
//         if (s === 1) {
//             if (!f.name.trim()) e.name = "Required";
//             if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Valid email required";
//             if (!f.mobile.trim()) e.mobile = "Required";
//             if (!f.institution.trim()) e.institution = "Required";
//             if (!f.role) e.role = "Select one";
//             if (f.role === "author") {
//                 if (!f.paperId1.trim()) e.paperId1 = "Required";
//                 if (!f.paperTitle1.trim()) e.paperTitle1 = "Required";
//                 if (f.papers === "2") {
//                     if (!f.paperId2.trim()) e.paperId2 = "Required";
//                     if (!f.paperTitle2.trim()) e.paperTitle2 = "Required";
//                 }
//             }
//         }
//         if (s === 2) {
//             if (!f.region) e.region = "Select one";
//             if (f.ieee === "yes" && !f.ieeeNumber.trim()) e.ieeeNumber = "Required";
//             if (!f.category) e.category = "Select a category";
//             if (f.hostel === "yes") {
//                 if (!f.hostelType) e.hostelType = "Required";
//                 if (!f.hostelDays) e.hostelDays = "Required";
//                 if (!f.checkIn) e.checkIn = "Required";
//                 if (!f.checkOut) e.checkOut = "Required";
//             }
//         }
//         if (s === 3) {
//             if (!f.sbiRef.trim()) e.sbiRef = "Required";
//             if (!f.payDate) e.payDate = "Required";
//             if (!f.receipt) e.receipt = "Receipt PDF required";
//             if (f.ieee === "yes" && !f.ieeeCard) e.ieeeCard = "IEEE card required";
//         }
//         setErrors(e);
//         return Object.keys(e).length === 0;
//     };

//     const next = () => validate(step) && setStep(step + 1);
//     const back = () => setStep(step - 1);

//     const onFile = (k) => (ev) => {
//         const file = ev.target.files[0];
//         if (!file) return set(k, null);
//         if (file.type !== "application/pdf") return setErrors((p) => ({ ...p, [k]: "PDF only" }));
//         if (file.size > MAX_FILE_MB * 1024 * 1024) return setErrors((p) => ({ ...p, [k]: `Max ${MAX_FILE_MB} MB` }));
//         setErrors((p) => ({ ...p, [k]: undefined }));
//         set(k, file);
//     };

//     const submit = async (ev) => {
//         ev.preventDefault();
//         if (!validate(3)) return;
//         setSubmitting(true);
//         setServerError("");
//         try {
//             const safeName = f.name.trim().replace(/\s+/g, "_");
//             const receiptName = f.role === "author" ? `Receipt_${safeName}_${f.paperId1}.pdf` : `Receipt_${safeName}.pdf`;
//             const payload = {
//                 ...f,
//                 receipt: undefined,
//                 ieeeCard: undefined,
//                 feeTier: fee.early ? "Early Bird" : "Standard",
//                 feeBase: fee.base,
//                 extraPages: fee.extraPages,
//                 extraCharge: fee.extra,
//                 gevCharge: fee.gev,
//                 feeTotal: fee.total,
//                 currency: fee.cur,
//                 courseCode: COURSE_CODE,
//                 receiptFile: { name: receiptName, data: await fileToBase64(f.receipt) },
//                 ieeeCardFile: f.ieeeCard ? { name: `IEEE_${safeName}.pdf`, data: await fileToBase64(f.ieeeCard) } : null,
//             };
//             const res = await fetch(APPS_SCRIPT_URL, {
//                 method: "POST",
//                 headers: { "Content-Type": "text/plain;charset=utf-8" },
//                 body: JSON.stringify(payload),
//             });
//             const json = await res.json();
//             if (!json.ok) throw new Error(json.error || "Submission failed");
//             setDone(true);
//         } catch (err) {
//             setServerError(err.message || "Something went wrong. Please try again.");
//         } finally {
//             setSubmitting(false);
//         }
//     };

//     const Err = ({ k }) => (errors[k] ? <span className="rf-err">{errors[k]}</span> : null);

//     if (done) {
//         return (
//             <div className="rf-page">
//                 <div className="rf-card rf-success">
//                     <h2>Registration Submitted</h2>
//                     <p>Thank you, <strong>{f.name}</strong>. A confirmation email has been sent to <strong>{f.email}</strong>.</p>
//                     <p>For queries: <a href="mailto:sanket@ecovillage.org.in">sanket@ecovillage.org.in</a></p>
//                 </div>
//             </div>
//         );
//     }

//     return (
//         <div className="rf-page">
//             <div className="rf-card">
//                 <header className="rf-head">
//                     <h1>SANKET 2026 – Conference Registration</h1>
//                     <p>4th &amp; 5th December 2026 · IIT Bombay, Mumbai</p>
//                     <div className="rf-steps">
//                         {["Participant", "Category & Fee", "Payment"].map((l, i) => (
//                             <span key={l} className={step === i + 1 ? "active" : step > i + 1 ? "done" : ""}>{i + 1}. {l}</span>
//                         ))}
//                     </div>
//                 </header>

//                 <form onSubmit={submit} noValidate>
//                     {/* ================= STEP 1 ================= */}
//                     {step === 1 && (
//                         <section className="rf-section">
//                             <h2>Participant Details</h2>
//                             <div className="rf-grid">
//                                 <label>Full Name *<input value={f.name} onChange={(e) => set("name", e.target.value)} /><Err k="name" /></label>
//                                 <label>Email ID *<input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /><Err k="email" /></label>
//                                 <label>Mobile Number *<input value={f.mobile} onChange={(e) => set("mobile", e.target.value)} /><Err k="mobile" /></label>
//                                 <label>Institution / Organisation *<input value={f.institution} onChange={(e) => set("institution", e.target.value)} /><Err k="institution" /></label>
//                             </div>

//                             <fieldset className="rf-radio">
//                                 <legend>I am registering as *</legend>
//                                 <label><input type="radio" checked={f.role === "author"} onChange={() => { set("role", "author"); set("category", ""); }} /> Author (presenting paper)</label>
//                                 <label><input type="radio" checked={f.role === "attendee"} onChange={() => { set("role", "attendee"); set("category", ""); }} /> Attendee (Student / Professional)</label>
//                                 <Err k="role" />
//                             </fieldset>

//                             {f.role === "author" && (
//                                 <>
//                                     <fieldset className="rf-radio">
//                                         <legend>Number of papers *</legend>
//                                         <label><input type="radio" checked={f.papers === "1"} onChange={() => { set("papers", "1"); set("category", ""); }} /> 1 paper</label>
//                                         <label><input type="radio" checked={f.papers === "2"} onChange={() => { set("papers", "2"); set("category", ""); }} /> 2 papers</label>
//                                     </fieldset>
//                                     <p className="rf-note">Authors presenting more than 2 papers must complete multiple registrations.</p>

//                                     <div className="rf-paper">
//                                         <h3>Paper 1</h3>
//                                         <div className="rf-grid">
//                                             <label>Paper ID *<input value={f.paperId1} onChange={(e) => set("paperId1", e.target.value)} /><Err k="paperId1" /></label>
//                                             <label>Number of pages *
//                                                 <select value={f.pages1} onChange={(e) => set("pages1", e.target.value)}>
//                                                     <option value="6">Up to 6 pages</option>
//                                                     <option value="7">7 pages</option>
//                                                     <option value="8">8 pages</option>
//                                                 </select>
//                                             </label>
//                                             <label className="rf-span">Paper Title *<input value={f.paperTitle1} onChange={(e) => set("paperTitle1", e.target.value)} /><Err k="paperTitle1" /></label>
//                                         </div>
//                                     </div>

//                                     {f.papers === "2" && (
//                                         <div className="rf-paper">
//                                             <h3>Paper 2</h3>
//                                             <div className="rf-grid">
//                                                 <label>Paper ID *<input value={f.paperId2} onChange={(e) => set("paperId2", e.target.value)} /><Err k="paperId2" /></label>
//                                                 <label>Number of pages *
//                                                     <select value={f.pages2} onChange={(e) => set("pages2", e.target.value)}>
//                                                         <option value="6">Up to 6 pages</option>
//                                                         <option value="7">7 pages</option>
//                                                         <option value="8">8 pages</option>
//                                                     </select>
//                                                 </label>
//                                                 <label className="rf-span">Paper Title *<input value={f.paperTitle2} onChange={(e) => set("paperTitle2", e.target.value)} /><Err k="paperTitle2" /></label>
//                                             </div>
//                                         </div>
//                                     )}
//                                     <p className="rf-note"><strong>Papers exceeding 8 pages are not permitted.</strong> Pages beyond 6 incur additional page charges (₹1,000 / $25 per page).</p>
//                                 </>
//                             )}

//                             <div className="rf-actions">
//                                 <button type="button" className="rf-btn primary" onClick={next}>Next →</button>
//                             </div>
//                         </section>
//                     )}

//                     {/* ================= STEP 2 ================= */}
//                     {step === 2 && (
//                         <section className="rf-section">
//                             <h2>Category &amp; Fee</h2>

//                             <fieldset className="rf-radio">
//                                 <legend>Nationality *</legend>
//                                 <label><input type="radio" checked={f.region === "Indian"} onChange={() => { set("region", "Indian"); set("category", ""); }} /> Indian</label>
//                                 <label><input type="radio" checked={f.region === "Foreign"} onChange={() => { set("region", "Foreign"); set("category", ""); }} /> Foreign</label>
//                                 <Err k="region" />
//                             </fieldset>

//                             <fieldset className="rf-radio">
//                                 <legend>IEEE Member? *</legend>
//                                 <label><input type="radio" checked={f.ieee === "yes"} onChange={() => set("ieee", "yes")} /> Yes</label>
//                                 <label><input type="radio" checked={f.ieee === "no"} onChange={() => { set("ieee", "no"); set("ieeeNumber", ""); set("ieeeCard", null); }} /> No</label>
//                             </fieldset>
//                             {f.ieee === "yes" && (
//                                 <label className="rf-single">IEEE Membership Number *<input value={f.ieeeNumber} onChange={(e) => set("ieeeNumber", e.target.value)} /><Err k="ieeeNumber" /></label>
//                             )}

//                             <label className="rf-single">Registration Category *
//                                 <select value={f.category} onChange={(e) => set("category", e.target.value)} disabled={!categories.length}>
//                                     <option value="">{categories.length ? "— Select —" : "Select role & nationality first"}</option>
//                                     {categories.map((c) => <option key={c} value={c}>{c}</option>)}
//                                 </select>
//                                 <Err k="category" />
//                             </label>

//                             {fee && (
//                                 <div className="rf-fee">
//                                     <div><span>Tier</span><strong>{fee.early ? "Early Bird" : "Standard"} · {f.ieee === "yes" ? "IEEE Member" : "Non-IEEE"}</strong></div>
//                                     <div><span>Registration Fee</span><strong>{fee.cur}{fmt(fee.base)}</strong></div>
//                                     {fee.extraPages > 0 && (
//                                         <div><span>Additional pages ({fee.extraPages} × {fee.cur}{fmt(PAGE_CHARGE[f.region])})</span><strong>{fee.cur}{fmt(fee.extra)}</strong></div>
//                                     )}
//                                     {fee.gev > 0 && (
//                                         <div><span>GEV Visit (6 Dec 2026)</span><strong>{fee.cur}{fmt(fee.gev)}</strong></div>
//                                     )}
//                                     <div className="rf-total"><span>Total Payable</span><strong>{fee.cur}{fmt(fee.total)}</strong></div>
//                                     <p className="rf-note">Inclusive of 18% GST. SBI Collect gateway charges are extra.</p>
//                                 </div>
//                             )}

//                             <fieldset className="rf-radio">
//                                 <legend>IIT Bombay Hostel Accommodation Request (request only — no charge now)</legend>
//                                 <label><input type="radio" checked={f.hostel === "yes"} onChange={() => set("hostel", "yes")} /> Yes, I request accommodation</label>
//                                 <label><input type="radio" checked={f.hostel === "no"} onChange={() => set("hostel", "no")} /> No</label>
//                             </fieldset>
//                             {f.hostel === "yes" && (
//                                 <div className="rf-grid">
//                                     <label>Room Type *
//                                         <select value={f.hostelType} onChange={(e) => set("hostelType", e.target.value)}>
//                                             <option value="">— Select —</option>
//                                             <option>Single</option>
//                                             <option>Shared</option>
//                                             <option>Guest House</option>
//                                         </select><Err k="hostelType" />
//                                     </label>
//                                     <label>Number of Days *<input type="number" min="1" value={f.hostelDays} onChange={(e) => set("hostelDays", e.target.value)} /><Err k="hostelDays" /></label>
//                                     <label>Check-in *<input type="datetime-local" value={f.checkIn} onChange={(e) => set("checkIn", e.target.value)} /><Err k="checkIn" /></label>
//                                     <label>Check-out *<input type="datetime-local" value={f.checkOut} onChange={(e) => set("checkOut", e.target.value)} /><Err k="checkOut" /></label>
//                                 </div>
//                             )}

//                             <fieldset className="rf-radio">
//                                 <legend>Govardhan EcoVillage (GEV) visit on 6 December 2026 — ₹1,200 (Indian) / $15 (Foreign)</legend>
//                                 <label><input type="radio" checked={f.gev === "yes"} onChange={() => set("gev", "yes")} /> Yes (charge added to total)</label>
//                                 <label><input type="radio" checked={f.gev === "no"} onChange={() => set("gev", "no")} /> No</label>
//                             </fieldset>
//                             {f.gev === "yes" && (
//                                 <p className="rf-note">
//                                     Please also fill the GEV visit form:{" "}
//                                     <a href={GEV_FORM_URL} target="_blank" rel="noopener noreferrer">Govardhan EcoVillage Visit Form ↗</a>
//                                 </p>
//                             )}

//                             <div className="rf-actions">
//                                 <button type="button" className="rf-btn secondary" onClick={back}>← Back</button>
//                                 <button type="button" className="rf-btn primary" onClick={next}>Next →</button>
//                             </div>
//                         </section>
//                     )}

//                     {/* ================= STEP 3 ================= */}
//                     {step === 3 && fee && (
//                         <section className="rf-section">
//                             <h2>Payment</h2>

//                             <div className="rf-fee">
//                                 <div className="rf-total"><span>Amount to pay on SBI Collect</span><strong>{fee.cur}{fmt(fee.total)}</strong></div>
//                                 <div><span>Course Code</span><strong>{COURSE_CODE}</strong></div>
//                             </div>

//                             <ol className="rf-guide">
//                                 <li>Open SBI Collect → <strong>Educational Institutions</strong>.</li>
//                                 <li>Search and select <strong>Indian Institute of Technology Kanpur</strong>.</li>
//                                 <li>Payment Category: <strong>Outreach Activities (OOA)</strong>.</li>
//                                 <li>Course Code <strong>{COURSE_CODE}</strong>, Title: Sustainability through Ancient Knowledge and Emerging Technologies (SANKET), Duration 04.12.2026 – 05.12.2026, Course Fee: <strong>{fee.cur}{fmt(fee.total)}</strong>.</li>
//                                 <li>Enter your Name, Email, Mobile; pay; download the receipt.</li>
//                             </ol>
//                             <div className="rf-ext-row">
//                                 <a className="rf-btn primary rf-ext" href={SBI_COLLECT_URL} target="_blank" rel="noopener noreferrer">Open SBI Collect ↗</a>
//                                 <a className="rf-btn secondary rf-ext" href={PAYMENT_GUIDE_URL} target="_blank" rel="noopener noreferrer">Payment Guide (PDF) ↗</a>
//                             </div>

//                             <div className="rf-grid">
//                                 <label>SBI Collect Reference Number *<input value={f.sbiRef} onChange={(e) => set("sbiRef", e.target.value)} /><Err k="sbiRef" /></label>
//                                 <label>Date of Payment *<input type="date" value={f.payDate} onChange={(e) => set("payDate", e.target.value)} /><Err k="payDate" /></label>
//                                 <label className="rf-span">Payment Receipt (PDF, max {MAX_FILE_MB} MB) *
//                                     <input type="file" accept="application/pdf" onChange={onFile("receipt")} />
//                                     <small>Will be saved as {f.role === "author" ? "Receipt_<name>_<PaperID>.pdf" : "Receipt_<name>.pdf"}</small>
//                                     <Err k="receipt" />
//                                 </label>
//                                 {f.ieee === "yes" && (
//                                     <label className="rf-span">IEEE Membership Card (PDF) *
//                                         <input type="file" accept="application/pdf" onChange={onFile("ieeeCard")} />
//                                         <Err k="ieeeCard" />
//                                     </label>
//                                 )}
//                             </div>

//                             {serverError && <p className="rf-err rf-server">{serverError}</p>}

//                             <div className="rf-actions">
//                                 <button type="button" className="rf-btn secondary" onClick={back} disabled={submitting}>← Back</button>
//                                 <button type="submit" className="rf-btn primary" disabled={submitting}>{submitting ? "Submitting…" : "Submit Registration"}</button>
//                             </div>
//                         </section>
//                     )}
//                 </form>
//             </div>
//         </div>
//     );
// };

// export default RegistrationForm;
